/**
 * File/Document Query Keys
 *
 * Centralized query key factory for all file and document-related queries.
 */

import type { FileFilters } from './types';

/**
 * File query key factory
 *
 * @example
 * ```typescript
 * // Get matter files
 * useQuery({ queryKey: fileKeys.forMatter(matterId), queryFn: ... })
 *
 * // Invalidate after upload
 * queryClient.invalidateQueries({ queryKey: fileKeys.forMatter(matterId) })
 * ```
 */
export const fileKeys = {
  /**
   * Root key for all file queries
   */
  all: ['files'] as const,

  /**
   * Base key for file lists
   */
  lists: () => [...fileKeys.all, 'list'] as const,

  /**
   * Files for a specific matter
   * @param matterId - The matter ID
   */
  forMatter: (matterId: number) => [...fileKeys.lists(), matterId] as const,

  /**
   * Filtered file list
   * @param matterId - The matter ID
   * @param filters - Optional filters
   */
  list: (matterId: number, filters?: FileFilters) =>
    filters
      ? ([...fileKeys.forMatter(matterId), filters] as const)
      : fileKeys.forMatter(matterId),

  /**
   * Specific file detail
   * @param matterId - The matter ID
   * @param fileId - The file ID
   */
  detail: (matterId: number, fileId: number) =>
    [...fileKeys.forMatter(matterId), 'item', fileId] as const,

  /**
   * File detail by ID only
   * @param fileId - The file ID
   */
  item: (fileId: number) => [...fileKeys.all, 'item', fileId] as const,

  /**
   * File download URL
   * @param fileId - The file ID
   */
  download: (fileId: number) => [...fileKeys.item(fileId), 'download'] as const,

  /**
   * Presigned URL for upload
   * @param fileKey - The file key
   */
  presignedUrl: (fileKey: string) =>
    [...fileKeys.all, 'presigned', fileKey] as const
} as const;

/**
 * Document query key factory (alias for files with document-specific keys)
 */
export const documentKeys = {
  /**
   * Root key for all document queries
   */
  all: ['documents'] as const,

  /**
   * Base key for document lists
   */
  lists: () => [...documentKeys.all, 'list'] as const,

  /**
   * Documents for a specific matter
   * @param matterId - The matter ID
   */
  forMatter: (matterId: number) => [...documentKeys.lists(), matterId] as const,

  /**
   * Filtered document list
   * @param matterId - The matter ID
   * @param filters - Optional filters
   */
  list: (matterId: number, filters?: FileFilters) =>
    filters
      ? ([...documentKeys.forMatter(matterId), filters] as const)
      : documentKeys.forMatter(matterId),

  /**
   * Specific document detail
   * @param documentId - The document ID
   */
  detail: (documentId: number) =>
    [...documentKeys.all, 'detail', documentId] as const,

  /**
   * Document with matter context
   * @param matterId - The matter ID
   * @param documentId - The document ID
   */
  item: (matterId: number, documentId: number) =>
    [...documentKeys.forMatter(matterId), 'item', documentId] as const,

  /**
   * Document summary for a matter
   * @param matterId - The matter ID
   */
  summary: (matterId: number) =>
    [...documentKeys.forMatter(matterId), 'summary'] as const,

  /**
   * Presigned URL for document
   * @param fileKey - The file key
   */
  presignedUrl: (fileKey: string) =>
    [...documentKeys.all, 'presigned', fileKey] as const
} as const;
